import React, { useState } from 'react'

import {
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Alert
} from '@mui/material'

import { positions } from '@mui/system';

import Chat, { Bubble, useMessages } from '@chatui/core'
import { useWhisper } from '@chengsokdara/use-whisper'
import { ElevenLabsTextSpeaker, WebTextSpeaker } from './TextSpeaker'

import Chart from 'chart.js/auto'
import { CategoryScale } from 'chart.js'
import { Data } from './utils/Data'

import JobForm from './components/JobForm'
import ChatOptions from './components/ChatOptions'
import ReplyButton from './components/ReplyButton'
import BarChart from './components/BarChart'
import { Bar } from 'react-chartjs-2'

import { fetchReply, fetchEval } from './utils/fetchReply'
import handleVoiceUpload from './utils/handleVoiceUpload'

import '@chatui/core/dist/index.css'

import logo from './images/logo.svg'

Chart.register(CategoryScale)

// TODO: Find better way to make textarea in <Chat/> component
// disabled. Currently disables ALL textareas.
function setGlobalStyle (property: any, value: any) {
  const style = document.createElement('style')
  style.type = 'text/css'
  style.innerHTML = `textarea { ${property}: ${value}; }`
  document.head.appendChild(style)
}

export default function App () {

  const initialLoggingMetrics = {
    holdBtnPressed: 0,
    beginChatCnt: 0,
    speakBtnPressed: 0
  }

  const [loggingMetrics, setLoggingMetrics] = useState(initialLoggingMetrics)
  const defaultJob = 'Front-end Software Engineer'
  const defaultExperience = '5 years of experience working at Google'
  const defaultName = 'John Doe'
  let { messages, resetList, appendMsg, setTyping } = useMessages([])
  const [experience, setExperience] = useState(defaultExperience)
  const [job, setJob] = useState(defaultJob)
  const [name, setName] = useState(defaultName)
  const [onSpeakSecsChecked, setOnSpeakSecsChecked] = useState(0)
  const [speakResponse, setSpeakResponse] = useState(false)
  const [waitingForResponse, setWaitingForResponse] = useState(true)
  const [analyticBtnPressed, setAnalyticBtnPressed] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [showStart, setShowStart] = useState(true)
  const [showInputError, setShowInputError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [msgCntOnAnalyticPress, setMsgCntOnAnalyticPress] = useState(0)
  const [metricError, setMetricError] = useState('')
  let showPinkBubble = true
  const [chartData, setChartData] = useState({
    labels: Data.map((data) => data.metric),
    datasets: [
      {
        label: 'Score',
        data: Data.map((data) => data.score),
        backgroundColor: [
          'rgba(75,192,192,1)',
          '#ecf0f1',
          '#50AF95',
          '#f3ba2f',
          '#2a71d0'
        ],
        borderColor: 'black',
        borderWidth: 2
      }
    ]
  })

  type Metrics = Record<string, any>
  // metrics: { [key: string]: any }
  const initMetrics: Metrics = {}

  const [metrics, setMetrics] = useState(initMetrics)
  console.log('original chartData: ')
  console.log(JSON.stringify(chartData))

  // Handle the transcription of the audio blob with custom backend
  // This is so we don't expose our API key to the client
  const onTranscribe = async (blob: Blob) => {
    try {
      const transcribedText = await handleVoiceUpload(blob)
      console.log('Got transcription:', transcribedText)
      await handleSend('text', transcribedText)

      return {
        blob,
        transcribedText
      }
    } catch (error: any) {
      console.error(error)
      setError('There was an error processing your voice. Try again.')
    }
    return {
      blob,
      text: ''
    }
  }

  const {
    recording,
    speaking,
    transcribing,
    transcript,
    pauseRecording,
    startRecording,
    stopRecording
  } = useWhisper({
    onTranscribe,
    removeSilence: true,
    nonStop: false
  })

  const addSystemReply = (reply: string) => {
    appendMsg({
      type: 'text',
      content: { text: reply },
      position: 'left'
    })
    setWaitingForResponse(false)
    setGlobalStyle('pointer-events', 'auto')
    // TODO: set different background color for textarea
    // setGlobalStyle('background-color', 'grey')
    if (speakResponse) {
      elevenSpeaker.speak(reply)
    }
  }

  const addHumanReply = (reply: string) => {
    appendMsg({
      type: 'text',
      content: { text: reply },
      position: 'right'
    })
  }

  const speaker = new WebTextSpeaker()
  const elevenSpeaker = new ElevenLabsTextSpeaker()
  let conversation_eval = ''

  const sendChat = async (text: string, metrics: boolean = false): Promise<string> => {
    const reply = ''

    try {
      setLoading(true)
      console.log('messages:')
      const history = messages.map((message) => {
        const author = message.position === 'left' ? 'System' : 'Human'
        return `${author}: ${message.content.text}`
      })

      if (metrics) {
        conversation_eval = await fetchEval(history, text, loggingMetrics)
        // try/catch to deal with non-json being sent back
        // usually because not enough data for LLM to transcribe
        try {
          setMetrics(JSON.parse(conversation_eval))
          const tempChartData = chartData
          tempChartData.datasets[0].data = Object.values(JSON.parse(conversation_eval))
          // pop off the summary, left with just scores
          tempChartData.datasets[0].data.pop()
          setChartData(tempChartData)
          // reset metric error
          setMetricError('')
        } catch (e: any) {
          console.log('metric error:')
          console.log(e.message)
          const preError = 'Error: didn\'t receive json object, received:\n'
          setMetricError(preError + conversation_eval)
        }
      } else {
        const reply = await fetchReply(history, text, job, experience, name)
        addSystemReply(reply)
        console.log(messages)
      }
    } catch (error: any) {
      setError(error)
    } finally {
      setLoading(false)
    }

    return reply
  }

  // will fetch metrics if metrics=true
  const handleEndButtonClick = async () => {
    await sendChat('', true)
    setShowChat(false)
    setAnalyticBtnPressed(false)
    setShowInputError(false)
    // I don't know why this works, but it does. Both resetList and messages = [] required.
    messages = []
    resetList(messages)
  }

  const handleAnalyticButtonClick = async () => {
    setAnalyticBtnPressed(true)
    setWaitingForResponse(true)
    setMsgCntOnAnalyticPress(messages.length)
    setGlobalStyle('pointer-events', 'none')
    await sendChat('', true)
    setWaitingForResponse(false)
    setGlobalStyle('pointer-events', 'auto')
  }
  const handleSend = async (type: string, val: string) => {
    if (type === 'text' && val.trim()) {
      addHumanReply(val)

      setWaitingForResponse(true)
      setGlobalStyle('pointer-events', 'none')
      // TODO: set different background color for textarea
      // setGlobalStyle('background-color', 'grey')
      setTyping(true)
      await sendChat(val)
    }
  }

  function renderMessageContent (msg: any) {
    showPinkBubble = !showPinkBubble
    const { content } = msg
    return (
      <Bubble style={{ backgroundColor: showPinkBubble ? 'pink' : 'white' }} content={content.text} />
    )
  }

  const jobFormProps = {
    loading,
    showStart,
    sendChat,
    showChat,
    setShowChat,
    setJob,
    setExperience,
    defaultJob,
    defaultExperience,
    showInputError,
    name,
    setName,
    loggingMetrics,
    setLoggingMetrics
  }

  return (
    <div>
      <AppBar position="static" style={{ backgroundColor: 'white' }} >
        <Toolbar>
          <img src={logo} style={{paddingRight:"6px"}} width="50" height="auto" alt="Conversa Logo" />
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <Typography variant="h4" style={{color: '#F80090', fontWeight: "bold"}}>Conversa</Typography>
            <Typography color="inherit" noWrap>
              &nbsp;
            </Typography>
            <Typography variant="h5" style={{color: '#F80090'}}>Job Coach</Typography>
          </div>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm">
        <Grid container spacing={2} sx={{ my: 4 }}>
          <JobForm {...jobFormProps} />
          {showChat && (
            <>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">Error: {error}</Alert>
                </Grid>
              )}

              <Grid item xs={12} sx={{ height: '400px' }}>
                <Chat
                  messages={messages}
                  locale="en-US"
                  placeholder="Type here..."
                  renderMessageContent={renderMessageContent}
                  onSend={handleSend}
                />
              </Grid>

              <Grid item xs={12}>
                <ReplyButton
                  disabled={waitingForResponse}
                  transcribing={transcribing}
                  onHold={startRecording}
                  onRelease={stopRecording}
                  onCancel={pauseRecording}
                  loggingMetrics={loggingMetrics}
                  setLoggingMetrics={setLoggingMetrics}
                />
                <Button
                  sx={{ marginLeft: '12px' }}
                  onClick={() => {
                    handleEndButtonClick()
                  }}
                  style={{ color: waitingForResponse ? 'grey' : '#F80090' }}
                  disabled={waitingForResponse}
                >
                    End Chat
                </Button>
                <Button
                  disabled={ messages.length < 2 || waitingForResponse || msgCntOnAnalyticPress === messages.length}
                  sx={{ marginLeft: '12px' }}
                  onClick={() => {
                    handleAnalyticButtonClick()
                  }}
                  style={{ color: (messages.length < 2 || waitingForResponse || msgCntOnAnalyticPress === messages.length) ? 'grey' : '#F80090' }}
                >
                  {analyticBtnPressed ? 'Update Analytics' : 'Show Analytics' }
                </Button>
              </Grid>

              <Grid item xs={12}>
                <ChatOptions
                  speakResponse={speakResponse}
                  onSpeakResponseChecked={setSpeakResponse}
                  loggingMetrics={loggingMetrics}
                  setLoggingMetrics={setLoggingMetrics}
                />
              </Grid>
              {metricError !== '' && (
                <Grid item xs={12}>
                  {metricError}
                </Grid>
              )}
              {metricError === '' && metrics.clarity !== undefined && analyticBtnPressed && (
                <Grid item xs={12}>
                  <BarChart chartData={chartData} />
                  <div>
                    <h2 style={{ textAlign: 'center' }}>Summary</h2>
                    <div style={{ marginBottom: '50px' }} >
                      {metrics.summary}
                    </div>
                  </div>
                </Grid>
              )}

            </>
          )}
        </Grid>
      </Container>
    </div>
  )
}
